<template>
  <!-- Show trial counter or subscription status -->
  <div v-if="shouldShowBanner" class="bg-white border-b shadow-sm">
    <div class="max-w-7xl mx-auto px-4 sm:px-6">
      <div
        class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 py-3 text-left"
      >
        <!-- Report a Problem Button (when having any issue) -->
        <div class="relative group w-full sm:w-auto">
          <button
            @click="openReportModal"
            style="font-size: 11px"
            aria-label="Report a problem"
            class="w-full lg:ml-[130px] sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 transition-colors duration-200 shadow-sm"
          >
            Report a Problem
          </button>

          <!-- Tooltip (Desktop Hover) -->
          <div
            class="absolute left-0 mt-2 w-64 bg-gray-900 text-white text-xs rounded-md p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 hidden sm:block"
          >
            <div>
              Broken button or glitch? Tell us here — we'll fix it fast.
            </div>
            <div class="mt-1">Suggestions welcome too.</div>
          </div>
        </div>

        <!-- Trial Status / Subscription Status and Action Buttons grouped together -->
        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4 w-full sm:w-auto"
        >
          <!-- Show subscription status if user has subscription -->
          <div
            v-if="hasSubscription && subscriptionStatus"
            class="flex items-center space-x-2"
          >
            <svg
              v-if="
                subscriptionStatus === 'Pending' ||
                subscriptionStatus === 'pending'
              "
              class="w-5 h-5 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span
              class="text-base font-medium"
              :class="{
                'text-yellow-700':
                  subscriptionStatus === 'Pending' ||
                  subscriptionStatus === 'pending',
                'text-green-700':
                  subscriptionStatus === 'Active' ||
                  subscriptionStatus === 'active',
              }"
            >
              <span
                v-if="
                  subscriptionStatus === 'Pending' ||
                  subscriptionStatus === 'pending'
                "
              >
                Subscription pending activation
                <span
                  v-if="trialStatus && trialStatus.isTrialActive"
                  class="text-gray-600"
                >
                  ({{ trialStatus.daysRemaining }}
                  {{ trialStatus.daysRemaining === 1 ? "day" : "days" }} left on
                  trial)
                </span>
              </span>
              <span v-else> Subscription Active </span>
            </span>
          </div>

          <!-- Show trial status if no subscription -->
          <div
            v-else-if="trialStatus && trialStatus.isTrialActive"
            class="flex items-center space-x-2 w-full sm:w-auto"
          >
            <svg
              class="w-5 h-5 text-blue-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span class="text-base font-medium text-gray-700 text-left">
              You have
              <span class="font-bold text-red-600">{{
                trialStatus.daysRemaining
              }}</span>
              {{ trialStatus.daysRemaining === 1 ? "day" : "days" }} left on
              your Free Trial.
            </span>
          </div>
          <div
            v-else-if="trialStatus && trialStatus.isTrialExpired"
            class="flex items-center space-x-2 w-full sm:w-auto"
          >
            <svg
              class="w-5 h-5 text-red-600 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span class="text-base font-medium text-red-600 text-left">
              Your free Trial has ended - Join Now
            </span>
          </div>

          <!-- Action Buttons -->
          <!-- View Subscription Button (when subscription exists) -->
          <button
            v-if="hasSubscription"
            @click="handleViewSubscription"
            class="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200 shadow-sm"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              ></path>
            </svg>
            <span>View Subscription</span>
          </button>

          <!-- Subscribe Now Button (when no subscription) -->
          <button
            v-else-if="shouldShowJoinNow"
            @click="handleJoinNow"
            class="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors duration-200 shadow-sm"
          >
            <span>Subscribe Now</span>
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- Report Problem Modal -->
  <teleport to="body">
    <div
      v-if="showReportModal"
      class="report-modal-container"
      :style="modalContainerStyle"
    >
      <div class="report-modal-overlay" @click="closeReportModal"></div>

      <div class="report-modal-content mx-4 sm:mx-0">
        <!-- Close X -->
        <button
          @click="closeReportModal"
          class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl"
        >
          &times;
        </button>

        <!-- Success State -->
        <div v-if="reportSuccess">
          <h2 class="text-xl font-semibold mb-2">Thanks — we received it.</h2>
          <p
            v-if="ticketId"
            class="text-sm text-gray-600 mb-4 text-center sm:text-left"
          >
            Ticket #{{ ticketId }}
          </p>
          <div class="flex justify-center sm:justify-start">
            <button
              @click="closeReportModal"
              class="px-4 py-2 bg-gray-800 text-white rounded-md"
            >
              Close
            </button>
          </div>
        </div>

        <!-- Form -->
        <div v-else>
          <h2 class="text-xl font-semibold mb-1 text-center sm:text-left">
            Report a Problem
          </h2>
          <p class="text-sm text-gray-600 mb-4 text-center sm:text-left">
            Something not working or have an idea to improve things? Send it
            here.
          </p>

          <!-- Type -->
          <div class="mb-4">
            <p class="font-medium mb-2">What is it?</p>
            <label class="flex items-center mb-2">
              <input
                type="radio"
                value="bug"
                v-model="reportType"
                class="mr-2"
              />
              Something isn't working (Bug)
            </label>
            <label class="flex items-center">
              <input
                type="radio"
                value="suggestion"
                v-model="reportType"
                class="mr-2"
              />
              Suggestion to improve
            </label>
          </div>

          <!-- Description -->
          <div class="mb-4">
            <label class="block font-medium mb-1">Describe it</label>
            <textarea
              v-model="reportDescription"
              rows="3"
              maxlength="1000"
              class="w-full border rounded-md p-2 text-sm focus:ring-1 focus:ring-gray-400 outline-none"
              placeholder="Tell us what happened (1–2 lines is fine)…"
            ></textarea>
            <div class="flex justify-between items-center mt-1">
              <p class="text-xs text-gray-500">
                Tip: include what you clicked + what you expected.
              </p>
              <p
                v-if="reportDescription.length > 800"
                :class="[
                  'text-xs',
                  reportDescription.length >= 1000
                    ? 'text-red-600 font-bold'
                    : 'text-gray-400',
                ]"
              >
                {{ reportDescription.length }} / 1000
              </p>
            </div>
          </div>

          <!-- Screenshot -->
          <div class="mb-4">
            <label class="block font-medium mb-1"
              >Screenshot (optional, max 10MB)</label
            >

            <div v-if="!screenshotPreview">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                @change="handleFileUpload"
                class="hidden"
                ref="fileInput"
              />
              <button
                type="button"
                @click="$refs.fileInput.click()"
                class="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition-all"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Attach screenshot
              </button>
            </div>

            <div
              v-else
              class="relative rounded-lg overflow-hidden border border-gray-200 group bg-gray-50"
            >
              <img :src="screenshotPreview" class="w-full h-32 object-cover" />
              <div
                class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                <button
                  @click="removeScreenshot"
                  class="px-3 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors"
                >
                  Remove
                </button>
              </div>
              <div
                class="p-2 border-t bg-white flex justify-between items-center"
              >
                <span class="text-xs text-gray-500 truncate max-w-[200px]">{{
                  screenshotName
                }}</span>
                <button
                  @click="removeScreenshot"
                  class="text-xs text-red-600 font-medium sm:hidden"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>

          <!-- Errors -->
          <p v-if="reportError" class="text-sm text-red-600 mb-3">
            {{ reportError }}
          </p>

          <!-- Buttons -->
          <div class="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-3">
            <button
              @click="closeReportModal"
              class="w-full sm:w-auto order-last sm:order-first px-4 py-2 bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              @click="submitReport"
              :disabled="isSubmitting"
              class="w-full sm:w-auto px-4 py-2 bg-gray-700 text-white rounded-md flex items-center justify-center min-w-[80px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <template v-if="isSubmitting">
                <svg
                  class="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </template>
              <template v-else> Submit </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import axiosInstance from "../utils/axios";
import { useToast } from "vue-toastification/dist/index.mjs";

export default {
  name: "TrialCounter",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      trialStatus: null,
      isLoading: false,
      hasSubscription: false, // Track if user has any subscription (Active or Pending)
      subscriptionStatus: null, // Store subscription status
      showReportModal: false,
      reportType: "",
      reportDescription: "",
      screenshotFile: null,
      screenshotName: "",
      screenshotPreview: null,
      reportError: "",
      reportSuccess: false,
      ticketId: null,
      isSubmitting: false,
    };
  },
  computed: {
    isSubscriptionActive() {
      return (
        this.trialStatus &&
        (this.trialStatus.subscriptionStatus === "active" ||
          this.trialStatus.subscriptionStatus === "Active")
      );
    },
    shouldShowJoinNow() {
      // Hide JOIN NOW button if user has any subscription (Active or Pending)
      if (this.hasSubscription) {
        return false;
      }
      // Show JOIN NOW if trial is active or expired and no subscription
      return (
        this.trialStatus &&
        (this.trialStatus.isTrialExpired || this.trialStatus.isTrialActive)
      );
    },
    shouldShowBanner() {
      // Show banner if:
      // 1. User has subscription (Active or Pending) - to show View Subscription button
      // 2. Trial is active or expired and no subscription - to show trial counter
      if (this.hasSubscription) {
        return true;
      }
      return (
        this.trialStatus &&
        (this.trialStatus.isTrialExpired || this.trialStatus.isTrialActive)
      );
    },
    modalContainerStyle() {
      // Determine sidebar width based on route
      let sidebarWidth = "0px";

      if (this.$route.path.startsWith("/admin")) {
        sidebarWidth = "18rem"; // w-72 (288px)
      } else {
        sidebarWidth = "22.5rem"; // w-90 (360px)
      }

      // Return style object
      // On mobile (controlled by media query in CSS), this left property will be overridden or handled
      // But since we are using inline style, we need to be careful.
      // Easiest is to set a CSS variable or use a class if possible.
      // But inline style 'left' is strong.
      // Let's rely on CSS media query with !important, OR better:
      // use a CSS variable for width.
      return {
        "--sidebar-width": sidebarWidth,
      };
    },
  },
  mounted() {
    this.fetchTrialStatus();
    this.checkSubscription();
    // Refresh every hour to update days remaining
    this.statusInterval = setInterval(() => {
      this.fetchTrialStatus();
      this.checkSubscription();
    }, 3600000); // 1 hour

    // Refresh when component becomes visible (e.g., after returning from payment)
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Listen for payment success event
    window.addEventListener("payment-success", this.handlePaymentSuccess);
  },
  beforeUnmount() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.removeEventListener("payment-success", this.handlePaymentSuccess);
  },
  methods: {
    async fetchTrialStatus() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axiosInstance.get("/api/trial/status");

        if (response.data.success) {
          this.trialStatus = response.data.data;
        }

        // Also check if user has a subscription (Active or Pending)
        await this.checkSubscription();
      } catch (error) {
        console.error("Error fetching trial status:", error);
        // Don't show error toast if user is not authenticated
        if (error.response?.status !== 401) {
          // Silently fail - trial counter is not critical
        }
      }
    },
    async checkSubscription() {
      try {
        const response = await axiosInstance.get("/api/profile");
        if (response.data.success && response.data.data.subscription) {
          const subscription = response.data.data.subscription;
          this.subscriptionStatus = subscription.subscriptionStatus;
          this.hasSubscription =
            subscription.subscriptionStatus === "Active" ||
            subscription.subscriptionStatus === "active" ||
            subscription.subscriptionStatus === "Pending" ||
            subscription.subscriptionStatus === "pending";
        } else {
          this.hasSubscription = false;
          this.subscriptionStatus = null;
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
        this.hasSubscription = false;
        this.subscriptionStatus = null;
      }
    },
    handleJoinNow() {
      this.$router.push("/subscription");
    },
    handleViewSubscription() {
      this.$router.push("/subscription");
    },
    handleVisibilityChange() {
      // Refresh trial status when page becomes visible (e.g., after payment redirect)
      if (!document.hidden) {
        this.fetchTrialStatus();
      }
    },
    handlePaymentSuccess() {
      // Refresh trial status immediately after payment success
      this.fetchTrialStatus();
      this.checkSubscription();
    },
    openReportModal() {
      this.showReportModal = true;
    },

    closeReportModal() {
      this.showReportModal = false;
      this.reportType = "";
      this.reportDescription = "";
      this.screenshotFile = null;
      this.screenshotName = "";
      this.screenshotPreview = null;
      this.reportError = "";
      this.reportSuccess = false;
      this.ticketId = null;
      this.isSubmitting = false;
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        this.reportError = "Please upload a PNG, JPG, or WebP image.";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.reportError =
          "That file is too large. Please choose an image under 10MB.";
        return;
      }

      this.screenshotFile = file;
      this.screenshotName = file.name;
      this.reportError = "";

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.screenshotPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removeScreenshot() {
      this.screenshotFile = null;
      this.screenshotName = "";
      this.screenshotPreview = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async submitReport() {
      if (!this.reportType || !this.reportDescription.trim()) {
        this.reportError =
          "Please choose an option and add a short description.";
        return;
      }

      this.isSubmitting = true;
      try {
        const formData = new FormData();
        formData.append("type", this.reportType);
        formData.append("description", this.reportDescription);
        formData.append("route", window.location.href);

        // Context Info
        const activeModule =
          this.$route.meta?.module ||
          this.$route.path.split("/")[1] ||
          "General";
        const activeScreen =
          this.$route.name || this.$route.path.split("/").pop() || "Unknown";
        formData.append("active_module", activeModule);
        formData.append("active_screen", activeScreen);

        // User & Account Info
        const userStr = localStorage.getItem("creator");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            if (user) {
              formData.append("user_email", user.email || "");
              formData.append(
                "plan",
                this.hasSubscription
                  ? this.subscriptionStatus
                  : this.trialStatus?.isTrialActive
                  ? "Trial"
                  : "Free",
              );
              formData.append("account_id", user._id || "");
            }
          } catch (e) {
            console.error("Error parsing user from localStorage:", e);
          }
        }

        // Browser & OS Info (Grouped into 'client' object)
        const ua = navigator.userAgent;
        let browserName = "Unknown";
        let browserVer = "";

        if (ua.indexOf("Chrome") > -1) {
          browserName = "Chrome";
          browserVer = ua.match(/Chrome\/(\d+)/)?.[1] || "";
        } else if (ua.indexOf("Firefox") > -1) {
          browserName = "Firefox";
          browserVer = ua.match(/Firefox\/(\d+)/)?.[1] || "";
        } else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) {
          browserName = "Safari";
          browserVer = ua.match(/Version\/(\d+)/)?.[1] || "";
        }

        let osName = "Unknown";
        let osVer = "";
        if (ua.indexOf("Win") > -1) {
          osName = "Windows";
          if (ua.indexOf("Windows NT 10.0") > -1) osVer = "10/11";
          else if (ua.indexOf("Windows NT 6.3") > -1) osVer = "8.1";
        } else if (ua.indexOf("Mac") > -1) {
          osName = "macOS";
          osVer =
            ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace(/_/g, ".") || "";
        } else if (ua.indexOf("Android") > -1) {
          osName = "Android";
          osVer = ua.match(/Android (\d+)/)?.[1] || "";
        } else if (ua.indexOf("like Mac") > -1) {
          osName = "iOS";
          osVer = ua.match(/OS (\d+[._]\d+)/)?.[1]?.replace(/_/g, ".") || "";
        }

        const viewport = `${window.innerWidth}x${window.innerHeight}`;
        let deviceType = "desktop";
        if (window.innerWidth < 768) deviceType = "mobile";
        else if (window.innerWidth < 1024) deviceType = "tablet";

        const clientData = {
          browser: `${browserName} ${browserVer}`.trim(),
          os: `${osName} ${osVer}`.trim(),
          device_type: deviceType,
          viewport: viewport,
        };

        formData.append("client", JSON.stringify(clientData));
        formData.append("app_version", "1.0.0"); // Update as needed

        if (this.screenshotFile)
          formData.append("screenshot", this.screenshotFile);

        const res = await axiosInstance.post("/api/report-problem", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.data.success) {
          this.ticketId = res.data.ticketId || null;
          this.reportSuccess = true;
        } else {
          this.reportError = "Couldn't send. Please try again.";
        }
      } catch (err) {
        if (err.response?.status === 429) {
          this.reportError =
            "You've sent a few reports recently. Please try again later.";
        } else {
          this.reportError = "Couldn't send. Please try again.";
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style>
/* Report Modal Styles - Matches Sidebar.vue Help Modal */
.report-modal-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  /* Full width to ensure overlay covers gaps */
  left: 0;
  /* Push content to center of remaining space */
  padding-left: var(--sidebar-width, 0);
  z-index: 40; /* Lower than sidebar (60) */
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* Let clicks pass through container to things below if needed, but overlay handles blocking */
  overflow: hidden;
}

.report-modal-overlay {
  position: absolute !important;
  inset: 0 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  pointer-events: auto; /* Block clicks */
  z-index: 45 !important;
}

.report-modal-content {
  position: relative;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 32rem; /* max-w-lg */
  /* max-height: 90vh; */
  /* overflow-y: auto; */
  pointer-events: auto;
  z-index: 50;
  padding: 1rem; /* Default padding for mobile */
}

@media (min-width: 640px) {
  .report-modal-content {
    padding: 1.5rem; /* p-6 for desktop */
  }
}

/* Mobile - remove padding */
@media (max-width: 1023px) {
  .report-modal-container {
    padding-left: 0 !important;
  }
}
</style>
